import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UndoIcon from '@mui/icons-material/Undo';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import {
  legacyImportService,
  LegacyClient,
  LegacyImportResult,
  LegacyMetric,
  LegacyRoutineEvent,
} from '@services/legacy-import.service.ts';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';

const fmt = (v: string | number | null | undefined): string =>
  v != null ? String(v) : '—';

const fmtDate = (v: unknown): string => {
  if (v == null) return '—';
  try {
    const d = new Date(v as string);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString('uk-UA');
  } catch {
    return String(v);
  }
};

const fullName = (c: LegacyClient): string =>
  [c.F, c.I, c.O].filter(Boolean).join(' ') || '—';

// --- Shared import panel ---

const ImportResultAlert: React.FC<{ result: LegacyImportResult }> = ({ result }) => (
  <Alert severity="success" sx={{ py: 0 }}>
    Рядків legacy: <b>{result.legacyRows}</b> · Спроб: <b>{result.attempted}</b> · Імпортовано: <b>{result.imported}</b> · Пропущено: <b>{result.skipped}</b> · Помилок: <b>{result.failed}</b>
  </Alert>
);

// --- Metrics tab ---

const MetricsTable: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [rows, setRows] = useState<LegacyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // import
  const [newUserId, setNewUserId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<LegacyImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // rollback confirm
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<{ deleted: number } | null>(null);

  useEffect(() => {
    setLoading(true);
    legacyImportService
      .getMetrics(clientId, { page, limit })
      .then(({ data, meta }) => { setRows(data); setTotal(meta.total); })
      .catch(() => setError('Не вдалося завантажити антропометрію.'))
      .finally(() => setLoading(false));
  }, [clientId, page, limit]);

  const handleImport = () => {
    const uid = parseInt(newUserId, 10);
    if (!uid) return;
    setImporting(true);
    setImportResult(null);
    setImportError(null);
    setRollbackResult(null);
    legacyImportService
      .importMetrics(clientId, uid)
      .then(setImportResult)
      .catch(() => setImportError('Помилка імпорту. Перевірте ID користувача та спробуйте ще раз.'))
      .finally(() => setImporting(false));
  };

  const handleRollbackConfirm = () => {
    const uid = parseInt(newUserId, 10);
    if (!uid) return;
    setRollingBack(true);
    setRollbackOpen(false);
    legacyImportService
      .rollbackMetrics(clientId, uid)
      .then((res) => { setRollbackResult(res); setImportResult(null); })
      .catch(() => setImportError('Помилка відкату імпорту.'))
      .finally(() => setRollingBack(false));
  };

  const userIdValid = !!newUserId && !isNaN(parseInt(newUserId, 10));

  return (
    <>
      {/* Import panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          label="ID користувача (нова система)"
          value={newUserId}
          onChange={(e) => {
            setNewUserId(e.target.value);
            setImportResult(null);
            setRollbackResult(null);
            setImportError(null);
          }}
          InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
          sx={{ width: 240 }}
          type="number"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          disabled={!userIdValid || importing || rollingBack}
          onClick={handleImport}
        >
          Імпортувати
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={rollingBack ? <CircularProgress size={14} color="inherit" /> : <UndoIcon />}
          disabled={!userIdValid || importing || rollingBack}
          onClick={() => setRollbackOpen(true)}
        >
          Відкотити імпорт
        </Button>

        {importResult && <ImportResultAlert result={importResult} />}
        {rollbackResult && (
          <Alert severity="info" sx={{ py: 0 }}>
            Видалено записів: <b>{rollbackResult.deleted}</b>
          </Alert>
        )}
        {importError && (
          <Alert severity="error" sx={{ py: 0 }}>{importError}</Alert>
        )}
      </Box>

      {loading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
      ) : !rows.length ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>Записів немає.</Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Вага</TableCell>
                  <TableCell>Талія</TableCell>
                  <TableCell>Груди</TableCell>
                  <TableCell>Стегна</TableCell>
                  <TableCell>Живіт</TableCell>
                  <TableCell>Рука</TableCell>
                  <TableCell>Зап'ястя</TableCell>
                  <TableCell>Гомілка</TableCell>
                  <TableCell>Жир %</TableCell>
                  <TableCell>Вода %</TableCell>
                  <TableCell>Вісц. жир</TableCell>
                  <TableCell>М'язи</TableCell>
                  <TableCell>Кістки</TableCell>
                  <TableCell>Метаболізм</TableCell>
                  <TableCell>Біо. вік</TableCell>
                  <TableCell>Коментар</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{fmt(r.date)}</TableCell>
                    <TableCell>{fmt(r.weight)}</TableCell>
                    <TableCell>{fmt(r.waist)}</TableCell>
                    <TableCell>{fmt(r.chest)}</TableCell>
                    <TableCell>{fmt(r.hips)}</TableCell>
                    <TableCell>{fmt(r.abdomen)}</TableCell>
                    <TableCell>{fmt(r.arm)}</TableCell>
                    <TableCell>{fmt(r.hand)}</TableCell>
                    <TableCell>{fmt(r.leg)}</TableCell>
                    <TableCell>{fmt(r.body_fat)}</TableCell>
                    <TableCell>{fmt(r.body_water)}</TableCell>
                    <TableCell>{fmt(r.visceral_fat)}</TableCell>
                    <TableCell>{fmt(r.muscle_mass)}</TableCell>
                    <TableCell>{fmt(r.bone_mass)}</TableCell>
                    <TableCell>{fmt(r.metabolic_rate)}</TableCell>
                    <TableCell>{fmt(r.biological_age)}</TableCell>
                    <TableCell>{fmt(r.comment)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            limitOptions={[10, 20, 50]}
            storageKey="legacy-import-metrics-pagination"
            onPageChange={setPage}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
          />
        </>
      )}

      {/* Rollback confirmation dialog */}
      <Dialog open={rollbackOpen} onClose={() => setRollbackOpen(false)}>
        <DialogTitle>Відкотити імпорт метрик?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Буде видалено всі записи антропометрії що були імпортовані з legacy для користувача <b>#{newUserId}</b>. Вручну додані записи не постраждають.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackOpen(false)}>Скасувати</Button>
          <Button color="error" variant="contained" onClick={handleRollbackConfirm}>Видалити</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Events tab ---

const EventsTable: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [rows, setRows] = useState<LegacyRoutineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  // import
  const [newUserId, setNewUserId] = useState('');
  const [newExpertId, setNewExpertId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<LegacyImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // rollback
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<{ deleted: number } | null>(null);

  useEffect(() => {
    setLoading(true);
    legacyImportService
      .getRoutineEvents(clientId, { page, limit })
      .then(({ data, meta }) => { setRows(data); setTotal(meta.total); })
      .catch(() => setError('Не вдалося завантажити події.'))
      .finally(() => setLoading(false));
  }, [clientId, page, limit]);

  const handleImport = () => {
    const uid = parseInt(newUserId, 10);
    const eid = parseInt(newExpertId, 10);
    if (!uid || !eid) return;
    setImporting(true);
    setImportResult(null);
    setImportError(null);
    setRollbackResult(null);
    legacyImportService
      .importRoutineEvents(clientId, uid, eid)
      .then(setImportResult)
      .catch(() => setImportError('Помилка імпорту. Перевірте ID та спробуйте ще раз.'))
      .finally(() => setImporting(false));
  };

  const handleRollbackConfirm = () => {
    const uid = parseInt(newUserId, 10);
    if (!uid) return;
    setRollingBack(true);
    setRollbackOpen(false);
    legacyImportService
      .rollbackRoutineEvents(clientId, uid)
      .then((res) => { setRollbackResult(res); setImportResult(null); })
      .catch(() => setImportError('Помилка відкату імпорту.'))
      .finally(() => setRollingBack(false));
  };

  const userIdValid = !!newUserId && !isNaN(parseInt(newUserId, 10));
  const expertIdValid = !!newExpertId && !isNaN(parseInt(newExpertId, 10));

  return (
    <>
      {/* Import panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          label="ID клієнта (нова система)"
          value={newUserId}
          onChange={(e) => { setNewUserId(e.target.value); setImportResult(null); setRollbackResult(null); setImportError(null); }}
          InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
          sx={{ width: 220 }}
          type="number"
        />
        <TextField
          size="small"
          label="ID експерта (нова система)"
          value={newExpertId}
          onChange={(e) => { setNewExpertId(e.target.value); setImportResult(null); setRollbackResult(null); setImportError(null); }}
          InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
          sx={{ width: 220 }}
          type="number"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          disabled={!userIdValid || !expertIdValid || importing || rollingBack}
          onClick={handleImport}
        >
          Імпортувати
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={rollingBack ? <CircularProgress size={14} color="inherit" /> : <UndoIcon />}
          disabled={!userIdValid || importing || rollingBack}
          onClick={() => setRollbackOpen(true)}
        >
          Відкотити імпорт
        </Button>

        {importResult && <ImportResultAlert result={importResult} />}
        {rollbackResult && (
          <Alert severity="info" sx={{ py: 0 }}>
            Видалено записів: <b>{rollbackResult.deleted}</b>
          </Alert>
        )}
        {importError && <Alert severity="error" sx={{ py: 0 }}>{importError}</Alert>}
      </Box>

      {loading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
      ) : !rows.length ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>Подій немає.</Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Дата/час</TableCell>
                  <TableCell>Назва</TableCell>
                  <TableCell>Клас</TableCell>
                  <TableCell>Б</TableCell>
                  <TableCell>Ж</TableCell>
                  <TableCell>В</TableCell>
                  <TableCell>Кл</TableCell>
                  <TableCell>Ккал</TableCell>
                  <TableCell>Підтверджено</TableCell>
                  <TableCell>Коментар</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{fmtDate(r.datetime)}</TableCell>
                    <TableCell>{fmt(r.event_name)}</TableCell>
                    <TableCell>{fmt(r.class)}</TableCell>
                    <TableCell>{fmt(r.proteins)}</TableCell>
                    <TableCell>{fmt(r.fats)}</TableCell>
                    <TableCell>{fmt(r.carbs)}</TableCell>
                    <TableCell>{fmt(r.cellulose)}</TableCell>
                    <TableCell>{fmt(r.kkal)}</TableCell>
                    <TableCell>{fmtDate(r.datetime_confirm)}</TableCell>
                    <TableCell>{fmt(r.comment)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            limitOptions={[25, 50, 100]}
            storageKey="legacy-import-events-pagination"
            onPageChange={setPage}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
          />
        </>
      )}

      {/* Rollback confirmation dialog */}
      <Dialog open={rollbackOpen} onClose={() => setRollbackOpen(false)}>
        <DialogTitle>Відкотити імпорт думок?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Буде видалено всі записи думок що були імпортовані з legacy для користувача <b>#{newUserId}</b> — як з власного календаря клієнта, так і з календаря експерта. Вручну додані записи не постраждають.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackOpen(false)}>Скасувати</Button>
          <Button color="error" variant="contained" onClick={handleRollbackConfirm}>Видалити</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Client details dialog ---

const ClientDialog: React.FC<{ client: LegacyClient; onClose: () => void }> = ({ client, onClose }) => {
  const [tab, setTab] = useState(0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {fullName(client)}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ID: {client.id} · {client.login}
          </Typography>
        </span>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Антропометрія" />
            <Tab label="Події" />
          </Tabs>
        </Box>
        <Box sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
          {tab === 0 && <MetricsTable clientId={client.id} />}
          {tab === 1 && <EventsTable clientId={client.id} />}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// --- Main page ---

const STATUS_LABELS: Record<number, string> = { 1: 'Активний', 2: 'Відмовився', 3: 'Видалений' };
const SEX_LABELS: Record<number, string> = { 1: 'Ч', 2: 'Ж' };

const FILTER_STORAGE_KEY = 'legacy-import-filters';
const PAGINATION_STORAGE_KEY = 'legacy-import-pagination';

export const AdminLegacyImportPage: React.FC = () => {
  const [clients, setClients] = useState<LegacyClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LegacyClient | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchClients = useCallback((searchValue: string, currentPage: number, currentLimit: number) => {
    setLoading(true);
    legacyImportService
      .getClients({ search: searchValue || undefined, page: currentPage, limit: currentLimit })
      .then(({ data, meta }) => { setClients(data); setTotal(meta.total); })
      .catch(() => setError('Не вдалося завантажити клієнтів. Перевірте чи увімкнено LEGACY_IMPORT_ENABLED на сервері.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchClients(search, page, limit);
  }, [page, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchClients(value, 1, limit), 400);
  };

  const handleReset = () => {
    setSearch('');
    setPage(1);
    fetchClients('', 1, limit);
  };

  return (
    <DashboardLayout>
      <Container maxWidth={false} data-i-doc={PageIndices.ADMIN_LEGACY_IMPORT}>
        <PageToolbar
          storageKey={FILTER_STORAGE_KEY}
          onReset={handleReset}
          
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за ПІБ',
              placeholder: 'Іваненко...',
              value: search,
              onChange: handleSearchChange,
              flexGrow: 0,
              minWidth: '320px',
            },
          ]}
        />

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Paper>
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>ПІБ</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Стать</TableCell>
                      <TableCell>Рік нар.</TableCell>
                      <TableCell>Зріст</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Остання антропометрія</TableCell>
                      <TableCell>Остання подія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map(c => (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelected(c)}
                      >
                        <TableCell>{c.id}</TableCell>
                        <TableCell>{fullName(c)}</TableCell>
                        <TableCell>{fmt(c.login)}</TableCell>
                        <TableCell>{c.sex != null ? (SEX_LABELS[c.sex] ?? c.sex) : '—'}</TableCell>
                        <TableCell>{fmt(c.born_year)}</TableCell>
                        <TableCell>{fmt(c.growth)}</TableCell>
                        <TableCell>{STATUS_LABELS[c.status] ?? c.status}</TableCell>
                        <TableCell>{fmt(c.last_antropometry)}</TableCell>
                        <TableCell>{fmtDate(c.last_event)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <UniversalPagination
              page={page}
              count={Math.ceil(total / limit)}
              limit={limit}
              storageKey={PAGINATION_STORAGE_KEY}
              onPageChange={setPage}
              onLimitChange={(l) => { setLimit(l); setPage(1); }}
            />
          </>
        )}

        {selected && <ClientDialog client={selected} onClose={() => setSelected(null)} />}
      </Container>
    </DashboardLayout>
  );
};
